import { useTranslation } from 'react-i18next';

type FormatStyle = 'short' | 'full';
type TimeUnit = 'hours' | 'minutes' | 'seconds';
type TimeUnitForFormatStyle = Record<FormatStyle, Record<TimeUnit, string>>;

const UNITS_PER_FORMAT_STYLE: TimeUnitForFormatStyle = {
    short: {
        hours: 'h',
        minutes: 'm',
        seconds: 's',
    },
    full: {
        hours: 'hour',
        minutes: 'minute',
        seconds: 'second',
    },
};

export const useFormattedDuration = (defaultStyle: FormatStyle = 'short') => {
    const { t } = useTranslation('common');

    const formatDuration = (
        isoDuration: string | undefined | null,
        style: FormatStyle = defaultStyle,
    ) => {
        if (!isoDuration) return isoDuration;

        const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
        if (!match) return isoDuration;

        const unitKeys = UNITS_PER_FORMAT_STYLE[style];

        const result = [
            { value: parseInt(match[1] || '0'), key: unitKeys.hours },
            { value: parseInt(match[2] || '0'), key: unitKeys.minutes },
            { value: parseInt(match[3] || '0'), key: unitKeys.seconds },
        ]
            .filter((part) => part.value > 0)
            .map((part) => {
                const separator = style === 'short' ? '' : ' ';
                const unit = t(`duration.${part.key}`, { count: part.value });

                return `${part.value}${separator}${unit}`;
            })
            .join(' ')
            .trim();

        return (
            result ||
            `0${style === 'short' ? '' : ' '}${t(`duration.${unitKeys.minutes}`, { count: 0 })}`
        );
    };

    return { formatDuration };
};
